import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export interface ShortcutCommand {
  keys: string[];
  description: string;
  action: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class KeyboardShortcutsService {
  private shortcuts = new Map<string, ShortcutCommand>();
  private pressedKeys = new Set<string>();
  private isEnabled = true;

  constructor(private translateService: TranslateService) {}

  /**
   * Register a keyboard shortcut
   */
  register(command: ShortcutCommand): void {
    // Generate the key for this command (sorted and joined)
    const keyString = [...command.keys].sort().join('+');
    this.shortcuts.set(keyString, command);
  }

  /**
   * Unregister a keyboard shortcut
   */
  unregister(keys: string[]): void {
    const keyString = [...keys].sort().join('+');
    this.shortcuts.delete(keyString);
  }

  /**
   * Clear all registered shortcuts
   */
  clearShortcuts(): void {
    this.shortcuts.clear();
  }

  /**
   * Enable keyboard shortcuts
   */
  enable(): void {
    this.isEnabled = true;
  }

  /**
   * Disable keyboard shortcuts
   */
  disable(): void {
    this.isEnabled = false;
  }

  /**
   * Get all registered shortcuts with translations
   */
  getShortcuts(): ShortcutCommand[] {
    return Array.from(this.shortcuts.values());
  }

  /**
   * Reset all pressed keys
   */
  resetKeys(): void {
    this.pressedKeys.clear();
  }

  /**
   * Process keyboard events
   */
  handleKeyDown(event: KeyboardEvent): void {
    if (!this.isEnabled) return;

    // Skip if the target is an input element (except for specific shortcuts)
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      // Only allow certain global shortcuts when in input fields
      if (!(event.ctrlKey && (event.key === 'Enter' || event.key === 'Escape'))) {
        return;
      }
    }

    // Add the pressed key to our set
    let key = event.key.toLowerCase();
    
    // Normalize modifiers for cross-browser compatibility
    if (key === 'control') key = 'ctrl';
    if (key === 'meta') key = 'cmd';
    
    this.pressedKeys.add(key);

    // Check if any registered shortcut matches the current pressed keys
    const currentKeys = [...this.pressedKeys].sort().join('+');
    const command = this.shortcuts.get(currentKeys);

    if (command) {
      event.preventDefault();
      command.action();
      // Reset after successful command
      this.resetKeys();
    }
  }

  /**
   * Remove a key when it's released
   */
  handleKeyUp(event: KeyboardEvent): void {
    let key = event.key.toLowerCase();
    
    // Normalize modifiers
    if (key === 'control') key = 'ctrl';
    if (key === 'meta') key = 'cmd';
    
    this.pressedKeys.delete(key);
    
    // If all modifier keys are released, reset
    if (!event.ctrlKey && !event.altKey && !event.shiftKey) {
      this.resetKeys();
    }
  }

  /**
   * Format keys for display
   */
  formatShortcut(keys: string[]): string {
    const isArabic = this.translateService.currentLang === 'ar';
    
    // Replace key names with their symbols for display
    return keys.map(key => {
      switch (key.toLowerCase()) {
        case 'ctrl': return isArabic ? 'Ctrl' : '⌃';
        case 'alt': return isArabic ? 'Alt' : '⌥';
        case 'shift': return isArabic ? 'Shift' : '⇧';
        case 'cmd': return isArabic ? 'Cmd' : '⌘';
        case 'enter': return isArabic ? 'Enter' : '↵';
        case 'esc': return isArabic ? 'Esc' : '⎋';
        case 'tab': return isArabic ? 'Tab' : '⇥';
        case 'space': return isArabic ? 'Space' : '␣';
        case 'backspace': return isArabic ? 'Backspace' : '⌫';
        default: return key.toUpperCase();
      }
    }).join(isArabic ? ' + ' : ' ');
  }
}